import { useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  addWatchlistCompany,
  addWatchlistQuery,
  archiveWatchlist,
  createWatchlist,
  fetchWatchlist,
  fetchWatchlistNewDocuments,
  fetchWatchlists,
  markWatchlistSeen,
  removeWatchlistCompany,
  removeWatchlistQuery,
  updateWatchlist,
} from "../lib/api";
import { useCan } from "../lib/session";
import type {
  WatchlistCompany,
  WatchlistDetailResponse,
  WatchlistFeedDocument,
  WatchlistPriority,
  WatchlistQuery,
  WatchlistQueryType,
  WatchlistSummary,
  WatchlistType,
  WatchlistUpdateRequest,
} from "../lib/types";

function shortWhen(value: string | null): string {
  if (!value) return "never";
  return value.slice(0, 16).replace("T", " ");
}

function cadenceLabel(value: number | null, fallback: number | null): string {
  if (value != null) return `${value} day${value === 1 ? "" : "s"}`;
  if (fallback != null) return `default (${fallback} day${fallback === 1 ? "" : "s"})`;
  return "priority default";
}

function memberCadenceLabel(value: number | null, effective: number): string {
  return value == null
    ? `default (${effective} day${effective === 1 ? "" : "s"})`
    : cadenceLabel(value, null);
}

function FeedItem({ doc }: { doc: WatchlistFeedDocument }) {
  return (
    <div className="result-item">
      <div className="result-title">
        <Link to={doc.deep_link.replace(/^\/app/, "") || "/"}>
          {doc.title ?? "Untitled document"}
        </Link>
      </div>
      <div className="result-meta">
        {[doc.source_name, doc.ticker, doc.doc_date, doc.page_count ? `${doc.page_count}pp` : null]
          .filter(Boolean)
          .join(" · ")}
        {" · ingested "}
        {shortWhen(doc.ingested_at)}
      </div>
    </div>
  );
}

function WatchlistFeed({ watchlistId }: { watchlistId: string }) {
  const firstPage = useQuery({
    queryKey: ["watchlist-feed", watchlistId],
    queryFn: () => fetchWatchlistNewDocuments(watchlistId),
  });
  // Pages fetched via "Load more" append below the first page; undefined
  // means "not paged yet, use the first page's cursor".
  const [extra, setExtra] = useState<WatchlistFeedDocument[]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const loadMore = useMutation({
    mutationFn: (next: string) => fetchWatchlistNewDocuments(watchlistId, next),
    onSuccess: (page) => {
      setExtra((prev) => [...prev, ...page.documents]);
      setCursor(page.next_cursor);
    },
  });

  if (firstPage.isLoading) {
    return <div className="empty">Loading new documents…</div>;
  }
  if (firstPage.isError || !firstPage.data) {
    return (
      <div className="empty">
        Feed failed — {(firstPage.error as Error | null)?.message ?? "unknown error"}
      </div>
    );
  }
  const documents = [...firstPage.data.documents, ...extra];
  const nextCursor = cursor === undefined ? firstPage.data.next_cursor : cursor;
  return (
    <>
      {documents.length === 0 ? (
        <div className="empty">Nothing new since {shortWhen(firstPage.data.since)}.</div>
      ) : (
        documents.map((doc) => <FeedItem key={doc.document_id} doc={doc} />)
      )}
      {nextCursor ? (
        <button
          className="btn"
          type="button"
          onClick={() => loadMore.mutate(nextCursor)}
          disabled={loadMore.isPending}
        >
          {loadMore.isPending ? "Loading…" : "Load more"}
        </button>
      ) : null}
      {loadMore.isError ? (
        <div className="empty">Load more failed — {(loadMore.error as Error).message}</div>
      ) : null}
    </>
  );
}

// Creates a watchlist through the same single writer the MCP tools use
// (athena.watchlist_admin -> athena.watchlists.get_or_create_watchlist).
function CreateWatchlistForm() {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [watchlistType, setWatchlistType] = useState<WatchlistType>("company");
  const create = useMutation({
    mutationFn: () => createWatchlist({ name: name.trim(), watchlist_type: watchlistType }),
    onSuccess: () => {
      setName("");
      void queryClient.invalidateQueries({ queryKey: ["watchlists"] });
    },
  });
  return (
    <form
      className="filter-row"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (name.trim() && !create.isPending) create.mutate();
      }}
    >
      <label>
        New watchlist
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="AI infrastructure"
          size={22}
          aria-label="New watchlist name"
        />
      </label>
      <label>
        Type
        <select
          value={watchlistType}
          onChange={(event) => setWatchlistType(event.target.value as WatchlistType)}
          aria-label="New watchlist type"
        >
          <option value="company">company</option>
          <option value="industry">industry</option>
          <option value="theme">theme</option>
          <option value="mixed">mixed</option>
        </select>
      </label>
      <button className="btn primary" type="submit" disabled={!name.trim() || create.isPending}>
        {create.isPending ? "Creating…" : "Create"}
      </button>
      {create.isError ? (
        <span className="empty">Create failed — {(create.error as Error).message}</span>
      ) : null}
    </form>
  );
}

function ReadOnlySettings({ detail }: { detail: WatchlistDetailResponse }) {
  const { watchlist } = detail;
  return (
    <>
      <div className="approval-banner" role="status">
        Read-only — you can inspect this watchlist, but your account cannot change it.
      </div>
      <dl className="metadata-list horizontal">
        <dt>Type</dt>
        <dd>{watchlist.watchlist_type}</dd>
        <dt>Status</dt>
        <dd>{watchlist.status}</dd>
        <dt>Priority</dt>
        <dd>{watchlist.priority}</dd>
        <dt>Description</dt>
        <dd>{watchlist.description || "—"}</dd>
      </dl>
    </>
  );
}

function WatchlistSettings({ detail }: { detail: WatchlistDetailResponse }) {
  const queryClient = useQueryClient();
  const { watchlist } = detail;
  const [name, setName] = useState(watchlist.name);
  const [description, setDescription] = useState(watchlist.description ?? "");
  const [watchlistType, setWatchlistType] = useState<WatchlistType>(watchlist.watchlist_type);
  const [priority, setPriority] = useState<WatchlistPriority>(watchlist.priority);
  const [dirty, setDirty] = useState({
    name: false,
    description: false,
    watchlistType: false,
    priority: false,
  });
  const hasChanges = Object.values(dirty).some(Boolean);
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["watchlists"] }),
      queryClient.invalidateQueries({ queryKey: ["watchlist", watchlist.watchlist_id] }),
      queryClient.invalidateQueries({ queryKey: ["watchlist-feed", watchlist.watchlist_id] }),
    ]);

  // A refetch may carry another editor's changes. Keep untouched controls in
  // sync, while preserving fields this editor is actively changing.
  useEffect(() => {
    if (!dirty.name) setName(watchlist.name);
    if (!dirty.description) setDescription(watchlist.description ?? "");
    if (!dirty.watchlistType) setWatchlistType(watchlist.watchlist_type);
    if (!dirty.priority) setPriority(watchlist.priority);
  }, [
    dirty.description,
    dirty.name,
    dirty.priority,
    dirty.watchlistType,
    watchlist.description,
    watchlist.name,
    watchlist.priority,
    watchlist.watchlist_type,
  ]);

  const update = useMutation({
    mutationFn: () => {
      const request: WatchlistUpdateRequest = {};
      if (dirty.name) request.name = name.trim();
      if (dirty.description) request.description = description.trim() || null;
      if (dirty.watchlistType) request.watchlist_type = watchlistType;
      if (dirty.priority) request.priority = priority;
      return updateWatchlist(watchlist.watchlist_id, request);
    },
    onSuccess: async () => {
      await refresh();
      setDirty({ name: false, description: false, watchlistType: false, priority: false });
    },
  });
  const archive = useMutation({
    mutationFn: () => archiveWatchlist(watchlist.watchlist_id),
    onSuccess: () => refresh(),
  });
  const confirmArchive = () => {
    const companies = `${detail.company_count} compan${detail.company_count === 1 ? "y" : "ies"}`;
    const queries = `${detail.query_count} quer${detail.query_count === 1 ? "y" : "ies"}`;
    if (
      window.confirm(
        `Archive shared watchlist "${watchlist.name}"? This removes it for everyone, including its ${companies} and ${queries}, from the active portal.`,
      )
    ) {
      archive.mutate();
    }
  };

  return (
    <section className="surface-section">
      <div className="section-head">
        <h2>List settings</h2>
        <span>{watchlist.status}</span>
      </div>
      <form
        className="formgrid"
        style={{ padding: "0 12px 8px" }}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          if (name.trim() && hasChanges && !update.isPending) update.mutate();
        }}
      >
        <label>
          Name
          <input
            value={name}
            onChange={(event) => {
              const next = event.target.value;
              setName(next);
              setDirty((current) => ({ ...current, name: next.trim() !== watchlist.name }));
            }}
            aria-label="Watchlist name"
            disabled={update.isPending || archive.isPending}
          />
        </label>
        <label>
          Type
          <select
            value={watchlistType}
            onChange={(event) => {
              const next = event.target.value as WatchlistType;
              setWatchlistType(next);
              setDirty((current) => ({
                ...current,
                watchlistType: next !== watchlist.watchlist_type,
              }));
            }}
            aria-label="Watchlist type"
            disabled={update.isPending || archive.isPending}
          >
            <option value="company">company</option>
            <option value="industry">industry</option>
            <option value="theme">theme</option>
            <option value="mixed">mixed</option>
          </select>
        </label>
        <label>
          Priority
          <select
            value={priority}
            onChange={(event) => {
              const next = event.target.value as WatchlistPriority;
              setPriority(next);
              setDirty((current) => ({ ...current, priority: next !== watchlist.priority }));
            }}
            aria-label="Watchlist priority"
            disabled={update.isPending || archive.isPending}
          >
            <option value="low">low</option>
            <option value="normal">normal</option>
            <option value="high">high</option>
            <option value="urgent">urgent</option>
          </select>
        </label>
        <label className="wide">
          Description
          <textarea
            rows={2}
            value={description}
            onChange={(event) => {
              const next = event.target.value;
              setDescription(next);
              setDirty((current) => ({
                ...current,
                description: (next.trim() || null) !== watchlist.description,
              }));
            }}
            aria-label="Watchlist description"
            disabled={update.isPending || archive.isPending}
          />
        </label>
        <div className="filter-row" style={{ gridColumn: "1 / -1", margin: 0 }}>
          <button
            className="btn primary"
            type="submit"
            disabled={!name.trim() || !hasChanges || update.isPending || archive.isPending}
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </button>
          <button
            className="btn"
            type="button"
            onClick={confirmArchive}
            disabled={archive.isPending || update.isPending}
          >
            {archive.isPending ? "Archiving…" : "Archive list"}
          </button>
          {update.isSuccess ? <span className="empty">Changes saved.</span> : null}
          {update.isError ? (
            <span className="empty">Save failed — {(update.error as Error).message}</span>
          ) : null}
          {archive.isError ? (
            <span className="empty">Archive failed — {(archive.error as Error).message}</span>
          ) : null}
        </div>
      </form>
    </section>
  );
}

function AddCompanyForm({ watchlistId }: { watchlistId: string }) {
  const queryClient = useQueryClient();
  const [ticker, setTicker] = useState("");
  const add = useMutation({
    mutationFn: () => addWatchlistCompany(watchlistId, { ticker: ticker.trim().toUpperCase() }),
    onSuccess: () => {
      setTicker("");
      void queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      void queryClient.invalidateQueries({ queryKey: ["watchlist", watchlistId] });
      void queryClient.invalidateQueries({ queryKey: ["watchlist-feed", watchlistId] });
    },
  });
  return (
    <form
      className="surface-toolbar"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (ticker.trim() && !add.isPending) add.mutate();
      }}
    >
      <label>
        Ticker
        <input
          value={ticker}
          onChange={(event) => setTicker(event.target.value)}
          placeholder="MU"
          aria-label="Company ticker"
        />
      </label>
      <button className="btn primary" type="submit" disabled={!ticker.trim() || add.isPending}>
        {add.isPending ? "Adding…" : "Add company"}
      </button>
      {add.isError ? (
        <span className="empty">Add company failed — {(add.error as Error).message}</span>
      ) : null}
    </form>
  );
}

function RemoveCompanyButton({
  watchlist,
  company,
}: {
  watchlist: WatchlistDetailResponse["watchlist"];
  company: WatchlistCompany;
}) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () =>
      removeWatchlistCompany(watchlist.watchlist_id, company.watchlist_company_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      void queryClient.invalidateQueries({ queryKey: ["watchlist", watchlist.watchlist_id] });
      void queryClient.invalidateQueries({
        queryKey: ["watchlist-feed", watchlist.watchlist_id],
      });
    },
  });
  const removeCompany = () => {
    if (window.confirm(`Remove ${company.ticker} from "${watchlist.name}"?`)) remove.mutate();
  };
  return (
    <>
      <button
        className="icon-btn danger"
        type="button"
        aria-label={`Remove ${company.ticker} from ${watchlist.name}`}
        title="Remove company"
        onClick={removeCompany}
        disabled={remove.isPending}
      >
        ×
      </button>
      {remove.isError ? <span className="danger-text">Remove failed</span> : null}
    </>
  );
}

function CompaniesTab({
  detail,
  writable,
}: {
  detail: WatchlistDetailResponse;
  writable: boolean;
}) {
  return (
    <>
      {writable ? <AddCompanyForm watchlistId={detail.watchlist.watchlist_id} /> : null}
      <section className="surface-section">
        <div className="section-head">
          <h2>Companies</h2>
          <span>{detail.company_count}</span>
        </div>
        <div className="table-scroll">
          <table className="data surface-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Priority</th>
                <th>Cadence</th>
                <th>Keywords</th>
                {writable ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {detail.companies.map((company) => (
                <tr key={company.watchlist_company_id}>
                  <td>
                    <Link className="ticker" to={`/companies/${encodeURIComponent(company.ticker)}`}>
                      {company.ticker}
                    </Link>
                    {company.status !== "active" ? (
                      <span className="chip gray">{company.status}</span>
                    ) : null}
                    {company.company_name ? (
                      <div className="muted-line">{company.company_name}</div>
                    ) : null}
                  </td>
                  <td>{company.priority ?? "default"}</td>
                  <td className="nowrap">
                    {memberCadenceLabel(company.cadence_days, company.effective_cadence_days)}
                  </td>
                  <td>{company.keywords.length ? company.keywords.join(", ") : "—"}</td>
                  {writable ? (
                    <td className="row-actions">
                      <RemoveCompanyButton watchlist={detail.watchlist} company={company} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {detail.companies.length === 0 ? (
          <div className="empty left">No companies in this watchlist.</div>
        ) : null}
      </section>
    </>
  );
}

function defaultQueryType(watchlistType: WatchlistType): WatchlistQueryType {
  if (watchlistType === "theme") return "theme";
  if (watchlistType === "industry") return "industry";
  return "topic";
}

function AddQueryForm({ detail }: { detail: WatchlistDetailResponse }) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [queryType, setQueryType] = useState<WatchlistQueryType>(
    defaultQueryType(detail.watchlist.watchlist_type),
  );
  const add = useMutation({
    mutationFn: () =>
      addWatchlistQuery(detail.watchlist.watchlist_id, {
        query: query.trim(),
        query_type: queryType,
      }),
    onSuccess: () => {
      setQuery("");
      void queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      void queryClient.invalidateQueries({
        queryKey: ["watchlist", detail.watchlist.watchlist_id],
      });
      void queryClient.invalidateQueries({
        queryKey: ["watchlist-feed", detail.watchlist.watchlist_id],
      });
    },
  });
  return (
    <form
      className="surface-toolbar"
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (query.trim() && !add.isPending) add.mutate();
      }}
    >
      <label>
        Theme or query
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="HBM supply agreements"
          aria-label="Theme or query"
        />
      </label>
      <label>
        Query type
        <select
          value={queryType}
          onChange={(event) => setQueryType(event.target.value as WatchlistQueryType)}
          aria-label="Query type"
        >
          <option value="industry">industry</option>
          <option value="theme">theme</option>
          <option value="topic">topic</option>
          <option value="custom">custom</option>
        </select>
      </label>
      <button className="btn primary" type="submit" disabled={!query.trim() || add.isPending}>
        {add.isPending ? "Adding…" : "Add query"}
      </button>
      {add.isError ? (
        <span className="empty">Add query failed — {(add.error as Error).message}</span>
      ) : null}
    </form>
  );
}

function RemoveQueryButton({
  watchlist,
  query,
}: {
  watchlist: WatchlistDetailResponse["watchlist"];
  query: WatchlistQuery;
}) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => removeWatchlistQuery(watchlist.watchlist_id, query.watchlist_query_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      void queryClient.invalidateQueries({ queryKey: ["watchlist", watchlist.watchlist_id] });
      void queryClient.invalidateQueries({
        queryKey: ["watchlist-feed", watchlist.watchlist_id],
      });
    },
  });
  const removeQuery = () => {
    if (window.confirm(`Remove "${query.query}" from "${watchlist.name}"?`)) remove.mutate();
  };
  return (
    <>
      <button
        className="icon-btn danger"
        type="button"
        aria-label={`Remove ${query.query} from ${watchlist.name}`}
        title="Remove query"
        onClick={removeQuery}
        disabled={remove.isPending}
      >
        ×
      </button>
      {remove.isError ? <span className="danger-text">Remove failed</span> : null}
    </>
  );
}

function QueriesTab({
  detail,
  writable,
}: {
  detail: WatchlistDetailResponse;
  writable: boolean;
}) {
  return (
    <>
      {writable ? <AddQueryForm detail={detail} /> : null}
      <section className="surface-section">
        <div className="section-head">
          <h2>Themes &amp; queries</h2>
          <span>{detail.query_count}</span>
        </div>
        <div className="table-scroll">
          <table className="data surface-table">
            <thead>
              <tr>
                <th>Theme or query</th>
                <th>Type</th>
                <th>Related</th>
                <th>Portals</th>
                <th>Priority</th>
                <th>Cadence</th>
                {writable ? <th>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {detail.queries.map((query) => (
                <tr key={query.watchlist_query_id}>
                  <td>
                    {query.query}
                    {query.status !== "active" ? (
                      <span className="chip gray">{query.status}</span>
                    ) : null}
                    {query.description || query.keywords.length ? (
                      <div className="muted-line">
                        {[query.description, ...query.keywords].filter(Boolean).join(" · ")}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <span className="chip gray">{query.query_type}</span>
                  </td>
                  <td>{query.related_tickers.length ? query.related_tickers.join(", ") : "—"}</td>
                  <td>{query.portals.length ? query.portals.join(", ") : "automatic"}</td>
                  <td>{query.priority ?? "default"}</td>
                  <td className="nowrap">
                    {memberCadenceLabel(query.cadence_days, query.effective_cadence_days)}
                  </td>
                  {writable ? (
                    <td className="row-actions">
                      <RemoveQueryButton watchlist={detail.watchlist} query={query} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {detail.queries.length === 0 ? (
          <div className="empty left">No themes or queries in this watchlist.</div>
        ) : null}
      </section>
    </>
  );
}

function NewDocumentsTab({ watchlistId }: { watchlistId: string }) {
  const queryClient = useQueryClient();
  const seen = useMutation({
    // Marks seen through the newest feed item the user could have seen (the
    // first page is newest-first), so a document ingested after render stays
    // unseen; without loaded feed data the server marks through now().
    mutationFn: () => {
      const feed = queryClient.getQueryData<{ documents: WatchlistFeedDocument[] }>([
        "watchlist-feed",
        watchlistId,
      ]);
      return markWatchlistSeen(watchlistId, feed?.documents[0]?.ingested_at ?? null);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["watchlists"] });
      void queryClient.invalidateQueries({ queryKey: ["watchlist-feed", watchlistId] });
    },
  });
  return (
    <section className="surface-section">
      <div className="section-head">
        <h2>New documents</h2>
        <button
          className="btn small"
          type="button"
          onClick={() => seen.mutate()}
          disabled={seen.isPending}
        >
          {seen.isPending ? "Marking…" : "Mark seen"}
        </button>
      </div>
      {seen.isError ? (
        <div className="empty">Mark seen failed — {(seen.error as Error).message}</div>
      ) : null}
      <WatchlistFeed watchlistId={watchlistId} />
    </section>
  );
}

type WatchlistTab = "companies" | "queries" | "documents";
const WATCHLIST_TABS: readonly WatchlistTab[] = ["companies", "queries", "documents"];

function WatchlistEditor({
  watchlist,
  writable,
}: {
  watchlist: WatchlistSummary;
  writable: boolean;
}) {
  const [tab, setTab] = useState<WatchlistTab>("companies");
  const detail = useQuery({
    queryKey: ["watchlist", watchlist.watchlist_id],
    queryFn: () => fetchWatchlist(watchlist.watchlist_id),
  });
  const tabId = (value: WatchlistTab) => `watchlist-${watchlist.watchlist_id}-${value}-tab`;
  const panelId = (value: WatchlistTab) => `watchlist-${watchlist.watchlist_id}-${value}-panel`;
  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = WATCHLIST_TABS.indexOf(tab);
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % WATCHLIST_TABS.length;
    if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + WATCHLIST_TABS.length) % WATCHLIST_TABS.length;
    }
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = WATCHLIST_TABS.length - 1;
    if (nextIndex == null) return;
    event.preventDefault();
    const next = WATCHLIST_TABS[nextIndex];
    setTab(next);
    document.getElementById(tabId(next))?.focus();
  };

  if (detail.isLoading) return <div className="empty">Loading watchlist…</div>;
  if (detail.isError || !detail.data) {
    return (
      <div className="empty">
        Watchlist detail failed — {(detail.error as Error | null)?.message ?? "unknown error"}
      </div>
    );
  }

  return (
    <>
      {writable ? <WatchlistSettings detail={detail.data} /> : <ReadOnlySettings detail={detail.data} />}
      <div className="tabchips" role="tablist" aria-label={`${detail.data.watchlist.name} sections`}>
        <button
          type="button"
          role="tab"
          id={tabId("companies")}
          aria-controls={panelId("companies")}
          aria-selected={tab === "companies"}
          tabIndex={tab === "companies" ? 0 : -1}
          className={tab === "companies" ? "active" : ""}
          onClick={() => setTab("companies")}
          onKeyDown={onTabKeyDown}
        >
          Companies ({detail.data.company_count})
        </button>
        <button
          type="button"
          role="tab"
          id={tabId("queries")}
          aria-controls={panelId("queries")}
          aria-selected={tab === "queries"}
          tabIndex={tab === "queries" ? 0 : -1}
          className={tab === "queries" ? "active" : ""}
          onClick={() => setTab("queries")}
          onKeyDown={onTabKeyDown}
        >
          Themes &amp; queries ({detail.data.query_count})
        </button>
        <button
          type="button"
          role="tab"
          id={tabId("documents")}
          aria-controls={panelId("documents")}
          aria-selected={tab === "documents"}
          tabIndex={tab === "documents" ? 0 : -1}
          className={tab === "documents" ? "active" : ""}
          onClick={() => setTab("documents")}
          onKeyDown={onTabKeyDown}
        >
          New documents
        </button>
      </div>
      {tab === "companies" ? (
        <div
          role="tabpanel"
          id={panelId("companies")}
          aria-labelledby={tabId("companies")}
        >
          <CompaniesTab detail={detail.data} writable={writable} />
        </div>
      ) : null}
      {tab === "queries" ? (
        <div role="tabpanel" id={panelId("queries")} aria-labelledby={tabId("queries")}>
          <QueriesTab detail={detail.data} writable={writable} />
        </div>
      ) : null}
      {tab === "documents" ? (
        <div role="tabpanel" id={panelId("documents")} aria-labelledby={tabId("documents")}>
          <NewDocumentsTab watchlistId={detail.data.watchlist.watchlist_id} />
        </div>
      ) : null}
    </>
  );
}

function WatchlistCard({
  watchlist,
  expanded,
  onToggle,
  writable,
}: {
  watchlist: WatchlistSummary;
  expanded: boolean;
  onToggle: () => void;
  writable: boolean;
}) {
  return (
    <section className="card">
      <div
        className="result-item"
        role="button"
        aria-expanded={expanded}
        tabIndex={0}
        style={{ cursor: "pointer" }}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="result-title">
          {watchlist.name}{" "}
          {watchlist.unseen_count > 0 ? (
            <span className="chip red">{watchlist.unseen_count} new</span>
          ) : (
            <span className="chip gray">up to date</span>
          )}
        </div>
        <div className="result-meta">
          {watchlist.description ? `${watchlist.description} · ` : ""}
          {watchlist.company_count} compan{watchlist.company_count === 1 ? "y" : "ies"} ·{" "}
          {watchlist.query_count} quer{watchlist.query_count === 1 ? "y" : "ies"} · seen through{" "}
          {shortWhen(watchlist.seen_through_ingested_at)}
        </div>
      </div>
      {expanded ? <WatchlistEditor watchlist={watchlist} writable={writable} /> : null}
    </section>
  );
}

export default function Watchlists() {
  const watchlists = useQuery({ queryKey: ["watchlists"], queryFn: fetchWatchlists });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const writable = useCan("watchlists.write");

  return (
    <>
      <div className="pagehead">
        <h1>Watchlists</h1>
        <div className="sub">
          Manage the companies and themes you follow, and review documents since you last marked
          each list seen.
        </div>
      </div>
      {writable ? <CreateWatchlistForm /> : null}
      {watchlists.isLoading ? <div className="empty">Loading watchlists…</div> : null}
      {watchlists.isError ? (
        <div className="empty">
          Watchlists failed — {(watchlists.error as Error).message}
        </div>
      ) : null}
      {watchlists.data && watchlists.data.watchlists.length === 0 ? (
        <div className="empty">No active watchlists yet.</div>
      ) : null}
      {(watchlists.data?.watchlists ?? []).map((watchlist) => (
        <WatchlistCard
          key={watchlist.watchlist_id}
          watchlist={watchlist}
          expanded={expandedId === watchlist.watchlist_id}
          writable={writable}
          onToggle={() =>
            setExpandedId((current) =>
              current === watchlist.watchlist_id ? null : watchlist.watchlist_id,
            )
          }
        />
      ))}
    </>
  );
}
